import { FormField, Input, TranslatableField, type FormChildrenProps } from "@rothoblaas/design-system";
import { FieldError, FieldValues, Path, useWatch } from "react-hook-form";
import useI18n from "../../hooks/useTranslations";
import { translatableFieldProps, TranslationFieldName } from "../../utils/translatableField";

type TranslatedName = Record<"name" | "nameIt" | "nameDe" | "nameEs" | "nameFr", string | null>;

interface TranslatableTextFieldProps<TForm extends TranslatedName & FieldValues> {
  form: FormChildrenProps<TForm>;
  /** The translated field (default "name"): the form has it and its translations (`<field>It`, `<field>De`, ...). */
  field?: TranslationFieldName<TForm>;
  /** Default: the common "name" label. */
  label?: string;
}

// Required text input of the admin forms (by default the name), with the dialog editing its translations.
const TranslatableTextField = <TForm extends TranslatedName & FieldValues>({
  form,
  field = "name" as TranslationFieldName<TForm>,
  label,
}: TranslatableTextFieldProps<TForm>) => {
  const i18n = useI18n();
  const values = useWatch({ control: form.control }) as TForm;
  const text = label ?? i18n.translation.common.name;

  return (
    <TranslatableField {...translatableFieldProps(field, values, form.setValue, i18n.translation)} dialogTitle={text}>
      <FormField label={text} error={form.formState.errors[field] as FieldError | undefined} isOptional={false}>
        {({ labelId, isOptional, isInvalid }) => (
          <Input
            id={labelId}
            isInvalid={isInvalid}
            {...form.register(field as unknown as Path<TForm>, {
              required: !isOptional,
              minLength: 1,
            })}
          />
        )}
      </FormField>
    </TranslatableField>
  );
};

export default TranslatableTextField;
